/**
 * Drag and Drop Hook for Workspace Items
 *
 * Provides state and handlers for dragging workspace items (tables, linked tables, folders)
 * and dropping them onto folders to move them.
 *
 * Supports dragging items from trash - will restore and move in one operation.
 *
 * @module features/workspace/hooks/useDragAndDrop
 */

import { useState, useCallback } from 'react';
import type { WorkspaceItemType } from '@loader/shared-types';

import type { WorkspaceItem } from '../components';

import {
  useMoveToFolderMutation,
  useBulkMoveToFolderMutation,
  useRestoreItemMutation,
} from '@/features/organization/hooks';
import { useToastStore } from '@/store/useToasts';


// ============================================================================
// TYPES
// ============================================================================

export interface DraggedItem {
  id: string;
  type: WorkspaceItemType;
  /** Whether the item is currently in trash */
  isInTrash?: boolean;
}

export interface UseDragAndDropOptions {
  /** All workspace items (used to check if dragged item is in trash) */
  items?: WorkspaceItem[];
  /** Currently selected items for batch operations */
  selectedItems?: Set<string>;
  /** Callback when move completes */
  onMoveComplete?: () => void;
}

export interface UseDragAndDropResult {
  // State
  draggedItem: DraggedItem | null;
  dragOverFolderId: string | null;

  // Item drag handlers
  handleItemDragStart: (e: React.DragEvent, id: string, type: WorkspaceItemType) => void;
  handleItemDragEnd: () => void;

  // Folder drop handlers
  handleFolderDragOver: (e: React.DragEvent, folderId: string) => void;
  handleFolderDragLeave: (e: React.DragEvent) => void;
  handleFolderDrop: (e: React.DragEvent, folderId: string | null) => void;

  // Check if item is being dragged
  isDraggingItem: (id: string) => boolean;
  isDraggingOverFolder: (folderId: string) => boolean;
}

// ============================================================================
// HOOK
// ============================================================================

/**
 * Hook for managing drag-and-drop of workspace items to folders
 */
export function useDragAndDrop(options: UseDragAndDropOptions = {}): UseDragAndDropResult {
  const { items, selectedItems, onMoveComplete } = options;
  const [draggedItem, setDraggedItem] = useState<DraggedItem | null>(null);
  const [dragOverFolderId, setDragOverFolderId] = useState<string | null>(null);

  const moveToFolderMutation = useMoveToFolderMutation();
  const bulkMoveToFolderMutation = useBulkMoveToFolderMutation();
  const restoreItemMutation = useRestoreItemMutation();
  const { success: showSuccess, error: showError } = useToastStore();

  // ============================================================================
  // ITEM DRAG HANDLERS
  // ============================================================================

  const handleItemDragStart = useCallback(
    (e: React.DragEvent, id: string, type: WorkspaceItemType) => {
      // Check if item is in trash
      const item = items?.find((i) => i.id === id);
      const isInTrash = !!item?.deletedAt;

      setDraggedItem({ id, type, isInTrash });
      e.dataTransfer.effectAllowed = 'move';

      // Set drag data (including trash status)
      e.dataTransfer.setData(
        'application/workspace-item',
        JSON.stringify({ id, type, isInTrash })
      );
      e.dataTransfer.setData('text/plain', id); // Fallback for compatibility

      // Check if this item is part of a selection
      const isDraggingSelected = selectedItems?.has(id);
      if (isDraggingSelected && selectedItems && selectedItems.size > 1) {
        // Set a flag indicating this is a batch drag
        e.dataTransfer.setData('batch-drag', 'true');
      }
    },
    [items, selectedItems]
  );

  const handleItemDragEnd = useCallback(() => {
    setDraggedItem(null);
    setDragOverFolderId(null);
  }, []);

  // ============================================================================
  // FOLDER DROP HANDLERS
  // ============================================================================

  const handleFolderDragOver = useCallback(
    (e: React.DragEvent, folderId: string) => {
      // Check if we have workspace item data
      const hasWorkspaceItem = e.dataTransfer.types.includes('application/workspace-item');
      if (!hasWorkspaceItem) return;

      e.preventDefault();
      e.stopPropagation();

      // Don't allow dropping on itself (if dragging a folder)
      if (draggedItem?.type === 'folder' && draggedItem?.id === folderId) {
        setDragOverFolderId(null);
        return;
      }

      e.dataTransfer.dropEffect = 'move';
      setDragOverFolderId(folderId);
    },
    [draggedItem]
  );

  const handleFolderDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    // Only clear if we're actually leaving the folder element
    const relatedTarget = e.relatedTarget as HTMLElement | null;
    if (!relatedTarget || !e.currentTarget.contains(relatedTarget)) {
      setDragOverFolderId(null);
    }
  }, []);

  const handleFolderDrop = useCallback(
    async (e: React.DragEvent, folderId: string | null) => {
      e.preventDefault();
      e.stopPropagation();

      // Check if we have workspace item data
      const itemDataStr = e.dataTransfer.getData('application/workspace-item');
      if (!itemDataStr) return;

      try {
        const itemData = JSON.parse(itemDataStr) as DraggedItem;

        // Check if this is a batch drag
        const isBatchDrag = e.dataTransfer.getData('batch-drag') === 'true';

        if (isBatchDrag && selectedItems && selectedItems.size > 1) {
          // Batch move all selected items
          const itemsToMove = Array.from(selectedItems).map(itemId => {
            // Find the type - we'll need to infer from context or store this info
            // For now, assume the dragged item type applies to all
            // In a real implementation, you might need to track item types separately
            return {
              itemType: itemData.type,
              itemId,
            };
          });

          bulkMoveToFolderMutation.mutate(
            { items: itemsToMove, folderId },
            {
              onSuccess: () => {
                showSuccess(`Moved ${itemsToMove.length} items${folderId ? ' to folder' : ' to root'}`);
                onMoveComplete?.();
              },
              onError: (err) => {
                showError(
                  'Failed to move items',
                  err instanceof Error ? err.message : 'Unknown error'
                );
              },
            }
          );
        } else if (itemData.isInTrash) {
          // Item is in trash - restore first, then move to folder
          try {
            // Restore from trash
            await restoreItemMutation.mutateAsync({
              itemType: itemData.type,
              itemId: itemData.id,
            });

            // Then move to the target folder
            await moveToFolderMutation.mutateAsync({
              itemType: itemData.type,
              itemId: itemData.id,
              folderId,
            });

            showSuccess(`Restored and moved item${folderId ? ' to folder' : ' to root'}`);
            onMoveComplete?.();
          } catch (err) {
            showError(
              'Failed to restore and move item',
              err instanceof Error ? err.message : 'Unknown error'
            );
          }
        } else {
          // Single item move (not in trash)
          moveToFolderMutation.mutate(
            { itemType: itemData.type, itemId: itemData.id, folderId },
            {
              onSuccess: () => {
                showSuccess(`Item moved${folderId ? ' to folder' : ' to root'}`);
                onMoveComplete?.();
              },
              onError: (err) => {
                showError(
                  'Failed to move item',
                  err instanceof Error ? err.message : 'Unknown error'
                );
              },
            }
          );
        }
      } catch (err) {
        console.error('Failed to parse drag data:', err);
      }

      setDraggedItem(null);
      setDragOverFolderId(null);
    },
    [
      selectedItems,
      moveToFolderMutation,
      bulkMoveToFolderMutation,
      restoreItemMutation,
      showSuccess,
      showError,
      onMoveComplete,
    ]
  );

  // ============================================================================
  // HELPER FUNCTIONS
  // ============================================================================

  const isDraggingItem = useCallback(
    (id: string) => {
      return draggedItem?.id === id;
    },
    [draggedItem]
  );

  const isDraggingOverFolder = useCallback(
    (folderId: string) => {
      return dragOverFolderId === folderId;
    },
    [dragOverFolderId]
  );

  return {
    draggedItem,
    dragOverFolderId,
    handleItemDragStart,
    handleItemDragEnd,
    handleFolderDragOver,
    handleFolderDragLeave,
    handleFolderDrop,
    isDraggingItem,
    isDraggingOverFolder,
  };
}
